import { Component, DestroyRef, inject, Input, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgTemplateOutlet, KeyValuePipe } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { Location, ManageLocationDialogData } from 'src/app/models/location.model';
import { LocationRelatableTypeEnum } from 'src/app/enums/location-relatable-type.enum';
import { ApiService, HttpQueryParams } from 'src/app/services/api.service';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { UtilsService } from 'src/app/services/utils.service';
import { ManageLocationDialogComponent } from '../manage-location-dialog/manage-location-dialog.component';
import { LocationDialogModeEnum } from 'src/app/enums/location-dialog-mode.enum';
import { PermissionsService } from 'src/app/services/permissions.service';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatSelect, MatOption, MatSelectChange } from '@angular/material/select';
import { User, UserSearchResult } from 'src/app/models/user.model';
import { PotentialClient, PotentialClientSearchResult } from 'src/app/models/potential-client.interface';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';
import { firstValueFrom, Observable, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CompanyChoice, CompanySearchResult } from 'src/app/models/company.model';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { CompanySearchDialogComponent } from '../../company-search-dialog/company-search-dialog.component';
import {
  resolveUserProfilePictureUrl,
  UserProfilePictureComponent,
} from 'src/app/components/utils/user-profile-picture/user-profile-picture.component';
import { UserService } from 'src/app/services/user.service';
import { EmployeeService } from 'src/app/services/employee.service';

@Component({
  selector: 'app-locations-management',
  templateUrl: './locations-management.component.html',
  styleUrls: ['./locations-management.component.css'],
  host: {
    '[class.title-hidden]': 'hide_title',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    MatButton,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatIconButton,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatChipSet,
    MatChip,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFormField,
    MatInput,
    MatSuffix,
    MatSelect,
    MatOption,
    MatLabel,
    KeyValuePipe,
    MatAutocompleteModule,
    MatAutocomplete,
    MatProgressSpinner,
    CompanySearchDialogComponent,
    UserProfilePictureComponent,
  ],
})
export class LocationsManagementComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  @ViewChild(CompanySearchDialogComponent) company_search_dialog?: CompanySearchDialogComponent;

  @Input({ required: true }) relatable_type!: LocationRelatableTypeEnum | null;
  @Input({ required: true }) relatable_id!: number | null;
  @Input() choose_relatable = false;
  @Input() from_locations_page: boolean = false;
  @Input() tab_layout = false;
  @Input() hide_title = false;

  LocationRelatableTypeEnum = LocationRelatableTypeEnum;
  relatable_types_labels: Record<LocationRelatableTypeEnum | 'null', string> = {
    [LocationRelatableTypeEnum.Company]: 'Empresa',
    [LocationRelatableTypeEnum.User]: 'Personal',
    [LocationRelatableTypeEnum.PotentialClient]: 'Cliente potencial',
    null: 'Genérica',
  };
  current_relatable_type: LocationRelatableTypeEnum | string = 'undefined';

  displayedColumns: string[] = ['name', 'ips', 'actions'];
  dataSource: MatTableDataSource<LocationCustom> = new MatTableDataSource();
  can_filter: boolean = false;

  override filterForm: UntypedFormGroup;

  override filterFormExtraParams = {
    relatable_type: ['undefined'],
    relatable_id: [''],
    user_id: [''],
  };

  user_search_focused = signal<boolean>(false);
  readonly user_search = new UntypedFormControl('');
  readonly user_search_term = signal('');
  readonly user_search_loading = signal(false);
  filtered_users: UserSearchResult[] = [];

  readonly potential_client_search = new UntypedFormControl('');
  readonly potential_client_search_term = signal('');
  readonly potential_client_search_loading = signal(false);
  filtered_potential_clients: PotentialClientSearchResult[] = [];

  active_company_products: CompanyProduct[] = [];

  private readonly api = inject(ApiService);
  private readonly permissions = inject(PermissionsService);
  private readonly dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly user_service = inject(UserService);
  private readonly employee_service = inject(EmployeeService);

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    // Do not call WorkerGuard here: canActivate() navigates to `/` for non-workers,
    // which breaks partners (and clients) when this component is embedded in detail pages.
    // Standalone locations page is already protected by WorkerGuard on the route.

    this.can_filter = this.from_locations_page && this.permissions.isAdmin();
    if (this.can_filter) {
      this.displayedColumns = ['name', 'logo_url', 'ips', 'actions'];
      this.initUserSearch();
      this.initPotentialClientSearch();
      this.loadActiveCompanyProducts();
    }

    super.ngOnInit();
  }

  override fetchData(): void {
    if (!this.can_filter) {
      this.filterForm.patchValue(
        {
          relatable_type: this.relatable_type ?? 'null',
          relatable_id: this.relatable_id ?? 'null',
        },
        { emitEvent: false }
      );
    }
    const params = {
      ...this.filterForm.value,
      with: 'location_ips',
      from_locations_page: this.from_locations_page,
    } as HttpQueryParams;

    this.api
      .getLocations(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: async (data: ArrayResponse<Location>): Promise<void> => {
          this.dataSource.data = await this.getLocationsCustom(data.data);
          this.setMetadata(+data.meta.total, +data.meta.current_page - 1, +data.meta.per_page);
        },
      });
  }

  add(): void {
    this.openLocationDialog();
  }

  edit(location: Location): void {
    this.openLocationDialog(location);
  }

  delete(location: Location): void {
    if (confirm(`¿Estás seguro de que quieres eliminar la ubicación "${location.name}"?`)) {
      this.api.deleteLocation(location.id).subscribe(() => {
        this.fetchData();
      });
    }
  }

  onRelatableTypeChange(event: MatSelectChange): void {
    if (this.current_relatable_type === LocationRelatableTypeEnum.User && event.value !== LocationRelatableTypeEnum.User) {
      this.filterForm.patchValue({ user_id: this.filterForm.get('relatable_id')?.value });
      this.filterForm.patchValue({ relatable_id: '' });
    } else if (this.current_relatable_type !== LocationRelatableTypeEnum.User && event.value === LocationRelatableTypeEnum.User) {
      this.filterForm.patchValue({ relatable_id: this.filterForm.get('user_id')?.value });
      this.filterForm.patchValue({ user_id: '' });
    } else {
      this.filterForm.patchValue({ relatable_id: '' });
    }

    if (event.value === 'null') {
      // generic location
      this.user_search.setValue('', { emitEvent: false });
      this.filterForm.patchValue({ user_id: '' });
    }

    if (
      event.value === 'undefined' ||
      event.value === LocationRelatableTypeEnum.Company ||
      event.value === LocationRelatableTypeEnum.User
    ) {
      this.displayedColumns = ['name', 'logo_url', 'ips', 'actions'];
    } else {
      this.displayedColumns = ['name', 'ips', 'actions'];
    }

    this.company_search_dialog?.reset();
    this.potential_client_search.setValue('', { emitEvent: false });

    this.filtered_potential_clients = [];

    this.current_relatable_type = event.value;
  }

  onUserSelected(event: MatAutocompleteSelectedEvent): void {
    const user = event.option.value as User;
    if (this.filterForm.get('relatable_type')?.value === LocationRelatableTypeEnum.User) {
      this.filterForm.patchValue({ relatable_id: user.id });
      this.filterForm.patchValue({ user_id: '' });
    } else if (this.filterForm.get('relatable_type')?.value === LocationRelatableTypeEnum.Company) {
      this.filterForm.patchValue({ relatable_id: '' });
      this.filterForm.patchValue({ user_id: user.id });
      this.company_search_dialog?.reset();
    } else if (this.filterForm.get('relatable_type')?.value === LocationRelatableTypeEnum.PotentialClient) {
      this.filterForm.patchValue({ relatable_id: '' });
      this.filterForm.patchValue({ user_id: user.id });
      this.potential_client_search.setValue('', { emitEvent: false });
    } else {
      this.filterForm.patchValue({ user_id: user.id });
    }
    this.user_search.setValue(this.displayUser(user), { emitEvent: false });
    this.user_search_term.set('');
    this.filtered_users = [];
  }

  displayUser(user: User | string | null): string {
    if (user == null || typeof user === 'string') {
      return user ?? '';
    }

    return user.full_name;
  }

  onCompanySelected(company: CompanyChoice): void {
    this.filterForm.patchValue({ relatable_id: company.id });
    this.filterForm.patchValue({ user_id: '' });
    this.user_search.setValue('', { emitEvent: false });
  }

  onCompanySearchCleared(): void {
    if (this.filterForm.get('relatable_type')?.value === LocationRelatableTypeEnum.Company) {
      this.filterForm.patchValue({ relatable_id: '' });
    }
  }

  onPotentialClientSelected(event: MatAutocompleteSelectedEvent): void {
    const potential_client = event.option.value as PotentialClient;
    this.filterForm.patchValue({ relatable_id: potential_client.id });
    this.filterForm.patchValue({ user_id: '' });
    this.user_search.setValue('', { emitEvent: false });
    this.potential_client_search.setValue(this.displayPotentialClient(potential_client), { emitEvent: false });
    this.potential_client_search_term.set('');
    this.filtered_potential_clients = [];
  }

  displayPotentialClient(potential_client: PotentialClient | string | null): string {
    if (potential_client == null || typeof potential_client === 'string') {
      return potential_client ?? '';
    }

    return potential_client.full_name;
  }

  private openLocationDialog(location: Location | null = null): void {
    const data: ManageLocationDialogData = location
      ? { mode: LocationDialogModeEnum.Edit, location }
      : this.choose_relatable
        ? { choose_relatable: true }
        : {
            mode: LocationDialogModeEnum.Create,
            relatable_type: this.relatable_type,
            relatable_id: this.relatable_id,
          };

    this.dialog
      .open(ManageLocationDialogComponent, {
        data: data satisfies ManageLocationDialogData,
        width: '980px',
      })
      .afterClosed()
      .subscribe(result => {
        if (result != null) {
          this.fetchData();
        }
      });
  }

  private initUserSearch(): void {
    this.user_search.valueChanges
      .pipe(
        tap(search => {
          const term = typeof search === 'string' ? search.trim() : '';
          this.user_search_term.set(term);
          if (term.length < 3) {
            this.filtered_users = [];
            this.user_search_loading.set(false);
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(search => {
          const term = typeof search === 'string' ? search.trim() : '';
          if (term.length < 3) {
            return of([] as UserSearchResult[]);
          }

          this.user_search_loading.set(true);
          return this.searchUsers(term).pipe(finalize(() => this.user_search_loading.set(false)));
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(users => {
        this.filtered_users = users;
      });
    this.user_search.valueChanges.subscribe(value => {
      if (value === '' || value === null) {
        if (this.filterForm.get('relatable_type')?.value === LocationRelatableTypeEnum.User) {
          this.filterForm.patchValue({ relatable_id: '' });
        }
        this.filterForm.patchValue({ user_id: '' });
      }
    });
  }

  private searchUsers(term: string): Observable<UserSearchResult[]> {
    return this.api
      .getActiveEmployees({
        search: term,
        select: 'id|name|surnames|pokemon_profile_image_url|serious_profile_image_url',
      })
      .pipe(
        takeUntilDestroyed(this.destroy_ref),
        map(response => {
          const users = (response.map(user => ({
            id: user.id,
            full_name: user.full_name,
            pokemon_profile_picture_url: user.pokemon_profile_picture_url,
            serious_profile_picture_url: user.serious_profile_picture_url,
          })) ?? []) as UserSearchResult[];

          return users.sort((left, right) => left.full_name.localeCompare(right.full_name));
        }),
        catchError(() => of([] as UserSearchResult[]))
      );
  }

  private loadActiveCompanyProducts(): void {
    this.api
      .getCompanyProducts({
        scopes: 'isActive',
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (data: ArrayResponse<CompanyProduct>) => {
          this.active_company_products = data.data ?? [];
        },
      });
  }

  private initPotentialClientSearch(): void {
    this.potential_client_search.valueChanges
      .pipe(
        tap(search => {
          const term = typeof search === 'string' ? search.trim() : '';
          this.potential_client_search_term.set(term);
          if (term.length < 3) {
            this.filtered_potential_clients = [];
            this.potential_client_search_loading.set(false);
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(search => {
          const term = typeof search === 'string' ? search.trim() : '';
          if (term.length < 3) {
            return of([] as PotentialClientSearchResult[]);
          }

          this.potential_client_search_loading.set(true);
          return this.searchPotentialClients(term).pipe(finalize(() => this.potential_client_search_loading.set(false)));
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(potential_clients => {
        this.filtered_potential_clients = potential_clients;
      });
    this.potential_client_search.valueChanges.subscribe(value => {
      if (value === '' || value === null) {
        if (this.filterForm.get('relatable_type')?.value === LocationRelatableTypeEnum.PotentialClient) {
          this.filterForm.patchValue({ relatable_id: '' });
        }
      }
    });
  }

  private searchPotentialClients(term: string): Observable<PotentialClientSearchResult[]> {
    return this.api
      .getPotentialClientsPaged({
        select: 'id|full_name|company_name',
        search: term,
      })
      .pipe(
        takeUntilDestroyed(this.destroy_ref),
        map(response => {
          const potential_clients = (response.data.map(potential_client => ({
            id: potential_client.id,
            full_name: potential_client.full_name,
            company_name: potential_client.company_name,
          })) ?? []) as PotentialClientSearchResult[];

          return potential_clients.sort((left, right) => left.full_name.localeCompare(right.full_name));
        }),
        catchError(() => of([] as PotentialClientSearchResult[]))
      );
  }

  private async getLocationsCustom(locations: Location[]): Promise<LocationCustom[]> {
    return await Promise.all(locations.map(location => this.getLocationCustom(location)));
  }

  private async getLocationCustom(location: Location): Promise<LocationCustom> {
    if (location.relatable_type === LocationRelatableTypeEnum.Company && location.relatable_id) {
      const company: CompanySearchResult = await firstValueFrom(
        this.api
          .getCompany(location.relatable_id.toString(), {
            with: 'company_products',
            select: 'id|fact_name,company_products.id|title|logo_url|company_id',
          })
          .pipe(takeUntilDestroyed(this.destroy_ref))
      );
      const logo_url =
        company.company_products?.find(
          product =>
            this.active_company_products.some(active_product => active_product.id === product.id) &&
            product.logo_url != null &&
            product.logo_url !== ''
        )?.logo_url ?? null;
      return { ...location, logo_url };
    }

    if (location.relatable_type === LocationRelatableTypeEnum.User && location.relatable_id) {
      const user: User = await firstValueFrom(
        this.api
          .getUser(location.relatable_id.toString(), { select: 'pokemon_profile_image_url|serious_profile_image_url' })
          .pipe(takeUntilDestroyed(this.destroy_ref))
      );
      return {
        ...location,
        logo_url: resolveUserProfilePictureUrl(
          user,
          this.user_service.getCurrentUser()?.role?.slug,
          this.employee_service.isMeetingOngoing()
        ),
      };
    }
    return { ...location, logo_url: null };
  }
}

interface LocationCustom extends Location {
  logo_url: string | null;
}
